/**
 * Copy of the Dashboard (`components/Dashboard/`). Hints say how each figure
 * is worked out, in the backend's terms; scope lines say what each section
 * covers, so no control reads as one over the whole page.
 */
export const dashboard = {
  title: 'Overview',
  dataUpdated: 'Data updated {{time}} · {{ago}}',
  dataUpdatedHint: "When the dashboard's figures were last rebuilt from the marketplaces' data",
  notBuilt: 'Data not built yet',
  stale: 'may be stale',
  refresh: 'Refresh',
  refreshing: 'Refreshing…',
  onThisPage: 'Sections',
  sections: {
    sales: 'Sales & marketing',
    focused: 'Focused SKUs',
    stock: 'Stock & inventory',
    ads: 'Advertising',
    tickets: 'Tickets',
    community: 'Community',
  },
  // What each section covers, under its title.
  scope: {
    sales: '{{from}} – {{to}} · {{places}} · amounts in {{currency}}',
    everywhere: 'every marketplace and store',
    stores_one: '{{count}} store',
    stores_other: '{{count}} stores',
    brands_one: '{{count}} brand',
    brands_other: '{{count}} brands',
    skus_one: '{{count}} SKU',
    skus_other: '{{count}} SKUs',
    focused: 'Your focused SKUs, in every store · each list says the days it covers',
    stock: 'As it is now, in every warehouse and marketplace',
    ads: 'The last 7 days against the 7 before, in every store',
    tickets: 'Your own work, for the month and week you pick',
    community: 'The forum, for the month you pick',
  },
  fx: {
    converted: 'Converted to {{currency}} from {{from}} at the rates of {{date}}',
    unknownDate: 'an unknown date',
    unconverted_one: '{{list}} left out: no exchange rate',
    unconverted_other: '{{list}} left out: no exchange rates',
  },
  filters: {
    label: 'Sales & marketing filters',
    dates: 'Date range',
    stores: 'Stores',
    allStores: 'All stores',
    // A marketplace's own row in the store list: picks every store in it.
    allOf: 'All of {{name}}',
    storesIn_one: '{{count}} store',
    storesIn_other: '{{count}} stores',
    // What tells two stores of one name apart: the account they sell from.
    sellerId: 'Seller {{id}}',
    otherMarketplace: 'Other marketplace',
    brands: 'Brands',
    allBrands: 'All brands',
    skus: 'SKUs',
    allSkus: 'All SKUs',
    skuSearch: 'Type a SKU code, name, EAN or model',
    skuTyping: 'Type at least 2 characters',
    skuNone: 'No SKU matches “{{query}}”',
    skuRemove: 'Remove {{code}}',
    skusPicked_one: '{{count}} SKU',
    skusPicked_other: '{{count}} SKUs',
    currency: 'Show in',
    currencyOf: 'Currency for {{section}}',
    reportingCurrency: 'Your organisation’s currency',
    // Two or more picks read as one count chip; two names do not fit the field.
    storesPicked_one: '{{count}} store',
    storesPicked_other: '{{count}} stores',
    brandsPicked_one: '{{count}} brand',
    brandsPicked_other: '{{count}} brands',
    apply: 'Apply',
    clear: 'Clear',
  },
  kpis: {
    label: 'Headline figures · pick one to chart it',
    names: {
      sales: 'Sales',
      units: 'Units',
      ad_spend: 'Ad spend',
      ad_sales: 'Ad sales',
      acos: 'ACOS',
      clicks: 'Ad clicks',
    },
    hints: {
      sales: 'Marketplace order lines in the window; cancelled lines hold none.',
      units: 'Units on the window’s marketplace order lines.',
      ad_spend: 'Advertising spend in the window.',
      ad_sales: 'Sales Amazon attributes to the ads in the window.',
      acos: 'Ad spend as a share of ad sales. Lower is better.',
      clicks: 'Clicks on the ads in the window.',
    },
    showing: 'In the chart',
    chartIt: 'Chart this',
    failed: 'Could not be loaded',
    noDays: 'No days to draw',
    noneInWindow: 'No {{metric}} in this window',
  },
  main: {
    view: 'Chart view',
    views: {
      forecast: 'Forecast',
      growth: 'Growth',
      groups: 'By {{dimension}}',
    },
    trendTitle: '{{metric}} · {{grain}}',
    forecastTitle: '{{metric}} · where the period is heading',
    growthTitle: '{{metric}} · month over month',
    groupsTitle: '{{metric}} by {{dimension}}',
    grains: { day: 'daily', week: 'weekly', month: 'monthly' },
    grain: 'Period',
    grainOptions: { day: 'Day', week: 'Week', month: 'Month' },
    inCurrency: 'In {{currency}}',
    notConverted: 'In {{shown}} — the backend could not convert to {{asked}}',
    kinds: {
      count: 'A count, the same in every currency',
      ratio: 'A ratio, the same in every currency',
    },
    empty: 'No periods to draw in this window.',
    noForecast:
      '{{metric}} has no forecast, so it is drawn as it went. Sales, ad spend and ad sales have one.',
    noGrowth:
      '{{metric}} has no growth series. Sales, ad spend and ad sales do: pick one of those.',
    everyGroup: 'Every group',
    moreGroups_one: '{{count}} more {{dimension}} in Every group',
    moreGroups_other: '{{count}} more in Every group',
  },
  noAccess: 'You do not have access to this section.',
  noAccessHint: 'It needs {{permission}} across the whole organization.',
  roasValue: '{{value}}x',
  openProduct: 'Open {{asin}} on the marketplace',
  metrics: {
    total_sales: 'Total sales',
    total_returns: 'Total returns',
    ams_sales: 'AMS sales',
    ams_spend: 'AMS spend',
    roas: 'ROAS',
  },
  map: {
    title: 'Where orders ship',
    hint: 'The window’s marketplace orders by ship-to country · darker is more',
    by: 'Measure',
    measures: { orders: 'Orders', units: 'Units', sales: 'Sales' },
    empty: 'No order in this window has a ship-to country.',
    chartTitle: 'Marketplace orders by ship-to country',
    loading: 'Loading the map…',
  },
  countries: {
    hint_one: '{{orders}} orders to {{count}} country',
    hint_other: '{{orders}} orders to {{count}} countries',
    country: 'Country',
    share: 'Share',
    mode: 'Show by country',
    modes: { sales: 'Sales', ads: 'Advertising' },
    salesTitle: 'Sales by country',
    adsTitle: 'Advertising by country',
    adsHint_one: '{{spend}} ad spend in {{count}} country',
    adsHint_other: '{{spend}} ad spend in {{count}} countries',
    adsEmpty: 'No advertising in this window.',
    acos: 'ACOS',
    measures: { spend: 'Ad spend', adSales: 'Ad sales', clicks: 'Clicks' },
  },
  forecast: {
    week: 'Weekly',
    month: 'Monthly',
    horizon: 'Forecast to',
    confidence: '{{value}} confidence',
    soFar: 'So far · since {{from}}',
    forecastTo: 'Forecast to {{day}}',
    range: 'Likely {{low}} – {{high}}',
    confidenceHint:
      'How right this same forecast was, over the same number of days, from four earlier starting points: 1 − the weighted absolute percentage error.',
    sum: '{{total}} = {{current}} so far + {{ahead}} forecast to {{day}}',
    insufficient: 'Not enough history to forecast',
    insufficientHint:
      'A forecast needs 60 days of history with some sales in them. The days so far are drawn without one.',
    today: 'Today',
    actual: 'Actual (daily)',
    forecast: 'Forecast',
    band: 'Forecast range',
  },
  flow: {
    title: '{{metric}} flow',
    hint: 'Turn steps on or off, and move them to change the order.',
    meta: 'In {{currency}}, from the total to each step',
    stepsNow: 'Flow steps: {{steps}}',
    noSteps: 'No steps',
    moveUp: 'Move {{level}} up',
    moveDown: 'Move {{level}} down',
    fallback: '{{metric}} has no flow of its own, so total sales’ is drawn',
    steps: 'Flow steps',
    levels: {
      category: 'Category',
      marketplace: 'Marketplace',
      store: 'Store',
      brand: 'Brand',
      product_category: 'Product category',
    },
    toggle: 'Show the {{level}} step',
    none: 'Turn on at least one step.',
    empty: 'Nothing to show for this window.',
    noFlow: 'This figure has no breakdown.',
    chartTitle: 'How {{metric}} splits',
    step: 'Step',
    value: 'Value',
    ofParent: 'Of {{name}}',
    ofTotal: 'Of the total',
  },
  growth: {
    subtitle: 'From {{from}} · projected to the end of the financial year ({{to}})',
    growth: 'Growth',
    decline: 'Decline',
    projected: 'Projected',
    trend: 'Trend',
    total: 'Total',
    insufficient: 'Not enough months to compare yet.',
    chartTitle: '{{metric}} growth, month on month',
  },
  focused: {
    movers: 'Focused SKUs getting worse',
    moversMeta_one: '{{count}} SKU · week of {{thisWeek}} against week of {{lastWeek}}',
    moversMeta_other: '{{count}} SKUs · week of {{thisWeek}} against week of {{lastWeek}}',
    rankBy: 'Rank by',
    moverMetrics: {
      rank: 'Amazon rank',
      sales: 'Sales',
      ams_spend: 'AMS spend',
      ams_clicks: 'AMS clicks',
      ams_sales: 'AMS sales',
      acos: 'ACOS',
    },
    lastWeek: 'Last week',
    thisWeek: 'This week',
    change: 'Change',
    moversEmpty: 'No focused SKU moved for the worse.',
    noSale: 'Focused SKUs with no sales',
    noSaleMeta_one: '{{count}} SKU with nothing sold since {{since}}',
    noSaleMeta_other: '{{count}} SKUs with nothing sold since {{since}}',
    noSaleEmpty: 'Every focused SKU sold something in the last 30 days.',
    marketplaceStock: 'Marketplace',
    warehouseStock: 'Warehouse',
    clicks: 'Clicks (30d)',
  },
  stock: {
    cover: 'Stock cover',
    bandCounts: {
      d0_30_one: '{{count}} SKU under 30 days',
      d0_30_other: '{{count}} SKUs under 30 days',
      d31_60_one: '{{count}} at 31–60 days',
      d31_60_other: '{{count}} at 31–60 days',
      d61_90_one: '{{count}} at 61–90 days',
      d61_90_other: '{{count}} at 61–90 days',
    },
    velocity: 'Units / day (30d)',
    days: 'Days of cover',
    daysValue: '{{value}}d',
    coverEmpty: 'No focused SKU is selling, so none has a cover to show.',
    suppressed: 'Suppressed inventory',
    suppressedMeta_one: '{{count}} SKU · {{units}} units no marketplace lists',
    suppressedMeta_other: '{{count}} SKUs · {{units}} units no marketplace lists',
    suppressedEmpty: 'Every SKU with stock is listed somewhere.',
    units: 'Units',
    warehouses: 'Warehouses',
    warehouseUnits: 'In our warehouses',
    warehouseUnitsHint: 'Available units now',
    amazonUnits: 'At Amazon',
    amazonUnitsHint: 'Sellable units, from the latest fresh report',
    onHand: 'On hand now',
    aging: 'Warehouse stock by brand and age',
    agingMeta_one: '{{count}} brand, largest first',
    agingMeta_other: 'Top 8 of {{count}} brands, largest first',
  },
  adsSection: {
    slots: 'Sponsored ad slots',
    slotsMeta_one: '{{count}} focused search term since {{since}}',
    slotsMeta_other: '{{count}} focused search terms since {{since}}',
    term: 'Search term',
    total: 'Total',
    winning: 'Winning',
    winRate: 'Win rate',
    winRateOf: 'Win rate for “{{term}}”',
    slotsEmpty: 'No focused search term was scraped in the last 7 days.',
    distressed: 'Distressed ad groups',
    distressedMeta_one: '{{count}} ad group · {{from}} – {{to}} against the week before',
    distressedMeta_other: '{{count}} ad groups · {{from}} – {{to}} against the week before',
    acos: 'ACOS',
    adGroup: 'Ad group',
    clicks: 'Clicks',
    conversionChange: 'Conv. change',
    budgetUse: 'Budget used',
    sales: 'Sales',
    spend: 'Spend',
    reason: 'Reason',
    reasons: {
      high_acos: 'High ACOS',
      conversion_drop: 'Conv. drop',
    },
    reasonText: {
      high_acos: 'Ad spend is {{acos}} of sales — {{times}}× your {{target}} target.',
      conversion_drop: 'Conversion fell {{drop}} — from {{before}} to {{now}} on the week before.',
    },
    distressedEmpty: 'No ad group is in distress.',
  },
  tickets: {
    worklist: 'Your ticket worklist',
    worklistMeta: 'Backlog now · resolved and reviewed {{from}} – {{to}}',
    month: 'Month',
    week: 'Week',
    weekLabel: 'Week {{n}} · {{from}}–{{to}}',
    type: 'Ticket type',
    total: 'Total',
    today: 'Today',
    columns: {
      unassigned: 'Unassigned',
      assigned_to_me: 'Assigned to me',
      pending: 'Pending',
      resolved: 'Resolved',
      reviewed: 'Reviewed',
      overdue: 'Overdue',
    },
    overdue_one: '{{count}} overdue',
    overdue_other: '{{count}} overdue',
    open: 'Open {{type}} tickets',
    openOverdue: 'Open overdue {{type}} tickets',
    empty: 'No tickets for you this week.',
    done: {
      title: 'Done — month to date',
      subtitle: 'Per day, in your time zone',
      solved: 'Solved',
      reviewed: 'Reviewed',
      assigned: 'Assigned',
      empty: 'Nothing yet this month.',
      chartTitle: 'Tickets {{metric}} per day',
    },
    snapshot: {
      title: 'My snapshot',
      solvedToday: 'Solved today',
      doneThisMonth: '{{metric}} this month',
      openOverdue: 'Open overdue',
      unassignedPool: 'Unassigned pool',
    },
  },
  forum: {
    title: 'Community forum',
    hint: 'What the community is talking about',
    open: 'Open forum',
    thread: 'Thread',
    comments: 'Comments',
    likes: 'Likes',
    created: 'Created',
    creator: 'Creator',
    sort: 'Sort threads by',
    sorts: { created: 'Newest', comments: 'Most comments', likes: 'Most likes' },
    contributors: 'Most contributed',
    contributorsHint: 'Posts and replies in the month',
    month: 'Month',
    posts_one: '{{count}} post',
    posts_other: '{{count}} posts',
    notConnected: 'The forum is not connected',
    notConnectedHint:
      'Its database is not configured for this environment, or did not answer. Threads and contributors show here once it is.',
    noThreads: 'No threads yet.',
    noContributors: 'Nobody has posted this month yet.',
    ago: '{{value}} ago',
    shareOfLeader: '{{name}}, against the month’s top contributor',
    openThread: 'Open “{{title}}” on the forum (opens in a new tab)',
  },
  table: {
    sku: 'SKU',
    brand: 'Brand',
    where: 'Marketplace / Store',
    noImage: 'No image',
    actions: 'Actions',
    noMatch: 'No rows match these column filters.',
    percentLabel: '{{label}} (%)',
  },
  // A table row's ways out, named for the row they open.
  actions: {
    analyzer: 'Open {{sku}} in the focused SKU analyzer',
    listing: 'Open {{sku}}’s listing on the marketplace (opens in a new tab)',
    sku: 'Find {{sku}} in internal SKUs',
    ageing: 'See {{sku}} in stock ageing',
    searchTerm: 'See “{{term}}” in search term history',
    adGroupView: 'Find {{name}} in AMS view',
    countryOrders: 'Open {{country}} orders in Live seller orders',
    countryAds: 'Open {{country}} ad groups in AMS view',
  },
  performance: {
    by: 'Group by',
    dimensions: {
      country: 'Country',
      family: 'Channel',
      brand: 'Brand',
      category: 'Category',
      subcategory: 'Subcategory',
    },
    kpis: {
      noFreshFba: 'No fresh Amazon report: stock older than 2 days isn’t counted',
    },
    table: {
      title: 'Every {{dimension}}',
      hint: 'Sales and advertising for the window in {{currency}} · stock as it is now',
      sales: 'Sales',
      units: 'Units',
      warehouseStock: 'Warehouse stock',
      fbaStock: 'Amazon stock',
      clicks: 'Clicks',
      adSpend: 'Ad spend',
      adSales: 'Ad sales',
      acos: 'ACOS',
      total: 'Total',
      empty: 'Nothing sold or advertised in this window.',
      unknown: 'Unknown',
      unassigned: 'Unassigned',
      truncated: 'Showing the first {{count}} groups.',
      ages: {
        age_0_30_units: '0–30 days',
        age_31_60_units: '31–60 days',
        age_61_90_units: '61–90 days',
        age_91_180_units: '91–180 days',
        age_181_270_units: '181–270 days',
        age_over_270_units: 'Over 270 days',
      },
      noStock: 'No stock',
    },
    // Channel names by the marketplace code's platform prefix.
    families: {
      AZ: 'Amazon',
      BL: 'Bol',
      BN: 'Bunnings',
      CR: 'Careem',
      DF: 'Direct Fulfilment',
      DT: 'Decathlon',
      EB: 'eBay',
      FK: 'Flipkart',
      KF: 'Kaufland',
      NN: 'Noon',
      OF: 'Offline',
      OT: 'Otto',
      SP: 'Shopify',
      TD: 'Trendyol',
      TT: 'TikTok Shop',
      WM: 'Walmart',
    },
  },
} as const
